

/**
 * ════════════════════════════════════════════════════════════════
 * contrast (r7.6) — ES-module mirror of services/media-service/src/lib/contrast.js (keep the two in step)
 * contrast (r7.6) — WCAG 2.x relative luminance + contrast ratio, and the AA report the palette editor
 * runs before a custom theme may be saved. Pure functions; mirrored in web-app-v3/src/lib/contrast.js so
 * the editor shows the same verdict the server enforces.
 *
 *   ratio('#0A0A0B', '#F5F5F4')  → 19.3
 *   aaReport(tokens)             → { ok, checks: [{ pair, fg, bg, ratio, min, ok, kind }] }
 *
 * Pairs checked (WCAG AA): body text on every surface ≥ 4.5, secondary text ≥ 4.5 on bg/bg-1, tertiary
 * text ≥ 3 (large/muted), accent-fg on accent ≥ 4.5 (buttons), accent on bg ≥ 3 (links/UI components),
 * semantic colours on bg ≥ 3, line-strong on bg ≥ 1.2 (visible border — not a WCAG rule, a usability floor).
 * ════════════════════════════════════════════════════════════════
 */

const HEX = /^#([0-9a-f]{6})$/i;
const RGBA = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(0|1|0?\.\d+)\s*)?\)$/i;
/** Tokens that may be translucent rgba() — they are composited over `bg` before the contrast check. */
const TRANSLUCENT_KEYS = Object.freeze(['line', 'line-strong', 'accent-soft']);

function hexToRgb(hex) {
  const m = HEX.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(rgb) { return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`.toUpperCase(); }

/** Resolve a token value to an opaque hex: hex stays, rgba() is composited over `over` (default black). */
function toOpaqueHex(value, over = '#000000') {
  if (HEX.test(String(value || '').trim())) return String(value).trim().toUpperCase();
  const m = RGBA.exec(String(value || '').trim());
  if (!m) return null;
  const a = m[4] === undefined ? 1 : Number(m[4]);
  const base = hexToRgb(over) || [0, 0, 0];
  const src = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (src.some((c) => c > 255)) return null;
  return rgbToHex(src.map((c, i) => c * a + base[i] * (1 - a)));
}

function isValidToken(key, value) {
  const v = String(value || '').trim();
  if (HEX.test(v)) return true;
  return TRANSLUCENT_KEYS.includes(key) && RGBA.test(v);
}

function channel(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function luminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function ratio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

/** The token set every theme must carry (matches web-app-v3/src/themes/themes.json). */
const TOKEN_KEYS = Object.freeze([
  'bg', 'bg-1', 'bg-2', 'bg-3', 'line', 'line-strong', 'text', 'text-2', 'text-3', 'text-4',
  'accent', 'accent-deep', 'accent-soft', 'accent-fg', 'ruby',
  'category-1', 'category-2', 'category-3', 'category-4', 'category-5',
  'success', 'warning', 'danger', 'info',
]);

const PAIRS = Object.freeze([
  { fg: 'text', bg: 'bg', min: 4.5, kind: 'AA text' },
  { fg: 'text', bg: 'bg-1', min: 4.5, kind: 'AA text' },
  { fg: 'text', bg: 'bg-2', min: 4.5, kind: 'AA text' },
  { fg: 'text', bg: 'bg-3', min: 4.5, kind: 'AA text' },
  { fg: 'text-2', bg: 'bg', min: 4.5, kind: 'AA text' },
  { fg: 'text-2', bg: 'bg-1', min: 4.5, kind: 'AA text' },
  { fg: 'text-3', bg: 'bg', min: 3, kind: 'AA large/muted' },
  { fg: 'accent-fg', bg: 'accent', min: 4.5, kind: 'AA button label' },
  { fg: 'accent', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'accent', bg: 'bg-1', min: 3, kind: 'AA UI component' },
  { fg: 'ruby', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'success', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'warning', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'danger', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'info', bg: 'bg', min: 3, kind: 'AA UI component' },
  { fg: 'line-strong', bg: 'bg', min: 1.2, kind: 'visible border' },
]);

/**
 * Validate a token map: every key present, every value #rrggbb, every pair at or above its minimum.
 * Returns { ok, missing, invalid, checks } — `ok` is false on any missing/invalid token or failed pair.
 */
function aaReport(tokens = {}) {
  const missing = TOKEN_KEYS.filter((k) => tokens[k] === undefined || tokens[k] === null || tokens[k] === '');
  const invalid = Object.entries(tokens).filter(([k, v]) => TOKEN_KEYS.includes(k) && !isValidToken(k, v)).map(([k]) => k);
  const checks = PAIRS.map((p) => {
    const bg = toOpaqueHex(tokens[p.bg], toOpaqueHex(tokens.bg) || '#000000');
    const fg = toOpaqueHex(tokens[p.fg], bg || '#000000');
    const r = ratio(fg, bg);
    return { pair: `${p.fg} on ${p.bg}`, fg: tokens[p.fg] || null, bg: tokens[p.bg] || null, ratio: r, min: p.min, kind: p.kind, ok: r !== null && r >= p.min };
  });
  const failed = checks.filter((c) => !c.ok);
  return { ok: !missing.length && !invalid.length && !failed.length, missing, invalid, failed: failed.map((c) => c.pair), checks };
}

/** Black or white — whichever reads better on the given colour (used to derive accent-fg for tenant palettes). */
function bestForeground(bg) {
  const dark = ratio(bg, '#0A0A0B');
  const light = ratio(bg, '#FFFFFF');
  if (dark === null || light === null) return '#0A0A0B';
  return dark >= light ? '#0A0A0B' : '#FFFFFF';
}

/** Mix a colour toward black (amount < 0) or white (amount > 0); amount in [-1, 1]. */
function shade(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const target = amount < 0 ? 0 : 255;
  const a = Math.min(1, Math.abs(amount));
  const out = rgb.map((c) => Math.round(c + (target - c) * a));
  return `#${out.map((c) => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}



export { HEX, RGBA, TOKEN_KEYS, TRANSLUCENT_KEYS, PAIRS, hexToRgb, rgbToHex, toOpaqueHex, isValidToken, luminance, ratio, aaReport, bestForeground, shade };
